/**
 * Meta (Facebook/Instagram) Pixel — browser side.
 *
 * @overview
 * Mirrors the shape of `@/lib/posthog`: environment-gated, no-op when
 * unconfigured, and the single place Meta standard events are named. Event
 * names and the dedup key come from `convex/lib/attribution` so the browser
 * pixel and the server-side Conversions API relay cannot drift apart.
 *
 * @usage
 * ```typescript
 * import { trackMetaEvent } from '@/lib/metaPixel';
 * import { META_EVENTS, buildEventId } from '../../convex/lib/attribution';
 *
 * trackMetaEvent(META_EVENTS.PURCHASE, { value: 49.99, currency: 'USD' },
 *   buildEventId(META_EVENTS.PURCHASE, orderNumber));
 * ```
 *
 * @security Never pass PII as pixel parameters. Customer matching happens
 * server-side in the Conversions API relay, where email is SHA-256 hashed
 * before transmission — the browser never sends an email to Meta.
 */

import { detectEnvironment } from './posthog';
import { hasAnalyticsConsent, onConsentChange } from './consent';
import type { MetaEventName } from '../../convex/lib/attribution';

const META_PIXEL_ID = import.meta.env.VITE_PUBLIC_META_PIXEL_ID;
/**
 * A SEPARATE Meta dataset for staging/local. Optional, and never the production
 * one — see `activeMetaPixelId()`.
 */
const META_PIXEL_ID_NONPROD = import.meta.env.VITE_PUBLIC_META_PIXEL_ID_NONPROD;

/** Meta's injected global. `unknown[]` because its signature is overloaded. */
type Fbq = ((...args: unknown[]) => void) & { callMethod?: unknown; queue?: unknown[] };

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

/**
 * Which Meta dataset this environment may write to, if any.
 *
 * The invariant that matters: **the production dataset is only ever reachable
 * from a production hostname.** PostHog can tag events with an `environment`
 * super property and filter test traffic out afterwards; Meta has no such
 * filter on the ad account, and a staging Purchase would permanently train the
 * conversion-optimisation model that spends real money.
 *
 * Staging and local are therefore not blocked outright — they are routed to a
 * SEPARATE dataset via `VITE_PUBLIC_META_PIXEL_ID_NONPROD`. That way the pixel
 * can actually be exercised before it reaches customers, instead of production
 * being the first environment it has ever run in, while a misconfiguration can
 * only ever pollute the throwaway dataset.
 *
 * Unset in non-production means the pixel stays inert, which is the safe default.
 */
/**
 * Placeholder values that are TRUTHY but are not pixel ids.
 *
 * Mirrors `placeholderValues` in `frontend/env.manifest.json`. This matters
 * concretely: the app-level `VITE_PUBLIC_META_PIXEL_ID` in Amplify is the
 * literal string `"unset"`, so without this check a branch that lacks its own
 * branch-level override would initialise the pixel with the id `"unset"` and
 * report events into nothing, looking healthy the whole time. Until now the
 * only thing preventing that was the hostname gate below, which is a lot of
 * weight for one condition to carry.
 */
const PLACEHOLDER_IDS = new Set([
  'unset',
  'changeme',
  'change-me',
  'todo',
  'tbd',
  'xxx',
  'none',
]);

function realPixelId(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return PLACEHOLDER_IDS.has(trimmed.toLowerCase()) ? undefined : trimmed;
}

export function selectMetaPixelId(
  hostname: string,
  productionId: string | undefined,
  nonProductionId: string | undefined
): string | undefined {
  return detectEnvironment(hostname) === 'production'
    ? realPixelId(productionId)
    : realPixelId(nonProductionId);
}

/**
 * One item in a Meta `contents` array.
 *
 * `id` MUST match the identifier used by the Meta product catalog, because
 * that is the join key for Advantage+ catalog ads and product-level audiences.
 * No catalog exists yet, so these are Convex product ids — whichever key a
 * catalog is eventually built on has to be THIS one, or the ads will match
 * nothing and there will be no error to tell you.
 */
export interface MetaContentItem {
  id: string;
  quantity: number;
  /** Unit price in DOLLARS, not cents. Meta's `item_price` is a decimal. */
  itemPriceDollars?: number;
}

interface MetaContentParams {
  content_type: 'product';
  content_ids: string[];
  contents: Array<{ id: string; quantity: number; item_price?: number }>;
}

/**
 * Build the product parameters Meta needs to do anything beyond counting.
 *
 * `value` and `currency` alone are enough to report revenue and nothing else.
 * Without `content_ids`/`contents`/`content_type` you cannot run catalog ads,
 * cannot build an audience of "viewed X but did not buy", and get no
 * product-level breakdown in reporting — which is most of the paid-social plan.
 *
 * Meta requires `id` and `quantity` on every `contents` entry, so items missing
 * an id are dropped rather than sent as partial rows that fail validation for
 * the whole event.
 */
export function buildContentParams(items: MetaContentItem[]): MetaContentParams {
  const valid = items.filter((item) => item.id && item.quantity > 0);
  return {
    content_type: 'product',
    content_ids: valid.map((item) => item.id),
    contents: valid.map((item) => ({
      id: item.id,
      quantity: item.quantity,
      ...(item.itemPriceDollars !== undefined ? { item_price: item.itemPriceDollars } : {}),
    })),
  };
}

export function activeMetaPixelId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return selectMetaPixelId(
    window.location.hostname,
    META_PIXEL_ID,
    META_PIXEL_ID_NONPROD
  );
}

/** True when this environment has a dataset it is permitted to write to. */
export function isMetaPixelEnabled(): boolean {
  return Boolean(activeMetaPixelId());
}

/** True when the pixel is both permitted and consented to. */
export function canTrackMeta(): boolean {
  return isMetaPixelEnabled() && hasAnalyticsConsent();
}

let initialised = false;

/**
 * Inject the pixel and fire the initial PageView.
 *
 * Safe to call repeatedly. Does nothing without consent — call it again from a
 * consent listener (see `startMetaPixelConsentBridge`) rather than pre-loading
 * the script and hoping it stays dormant.
 */
export function initMetaPixel(): void {
  if (initialised || !canTrackMeta()) return;

  injectPixelScript();
  window.fbq?.('init', activeMetaPixelId());
  window.fbq?.('track', 'PageView');
  initialised = true;
}

/**
 * Initialise now if already consented, and again if consent is granted later.
 * Returns an unsubscribe function. Call once during app start-up.
 */
export function startMetaPixelConsentBridge(): () => void {
  initMetaPixel();
  return onConsentChange((state) => {
    if (state === 'granted') initMetaPixel();
  });
}

/**
 * Record a Meta standard event.
 *
 * `eventId` is required for anything the server also reports. Meta deduplicates
 * a browser event against a Conversions API event with the same `eventID`, and
 * without it a single purchase is counted twice — inflating exactly the ROAS
 * number that decides whether to raise budget.
 */
export function trackMetaEvent(
  event: MetaEventName,
  properties?: Record<string, unknown>,
  eventId?: string
): void {
  if (!canTrackMeta() || !window.fbq) return;
  window.fbq('track', event, properties ?? {}, eventId ? { eventID: eventId } : undefined);
}

/** Pageview on client-side navigation. The SPA router drives this, not Meta. */
export function trackMetaPageView(): void {
  if (!canTrackMeta() || !window.fbq) return;
  window.fbq('track', 'PageView');
}

/**
 * Meta's official loader snippet, transcribed rather than injected as a raw
 * `<script>` string so the CSP does not need `unsafe-inline` widened for it.
 *
 * Requires `https://connect.facebook.net` in `script-src` — see the CSP in
 * `amplify.yml` (deployed) and `vite.config.ts` (dev server). Without it the
 * script is blocked and every event silently no-ops.
 */
function injectPixelScript(): void {
  if (window.fbq) return;

  const fbq: Fbq = function (...args: unknown[]) {
    // Before the real library loads, calls are buffered on .queue; afterwards
    // Meta replaces callMethod and this shim forwards straight through.
    const self = window.fbq as Fbq;
    if (typeof self.callMethod === 'function') {
      (self.callMethod as (...a: unknown[]) => void)(...args);
    } else {
      (self.queue as unknown[]).push(args);
    }
  } as Fbq;

  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
}

/** Test seam: forget that init ran. Not used by application code. */
export function __resetMetaPixelForTest(): void {
  initialised = false;
}
